import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import AdminSearch from '../../components/admin/AdminSearch';

export default function AdminTransactions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ items: [], pages: 1 });
  const [loading, setLoading] = useState(true);
  const search = searchParams.get('search') || '';
  const [searchInput, setSearchInput] = useState(search);
  const status = searchParams.get('status') || '';
  const page = Number(searchParams.get('page') || 1);

  useEffect(() => {
    setLoading(true);
    const params = { page, limit: 20 };
    if (status) params.status = status;
    if (search) params.search = search;
    api.get('/payments/transactions', { params }).then((res) => setData(res.data)).finally(() => setLoading(false));
  }, [status, search, page]);

  const updateSearch = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('search', value); else next.delete('search');
    next.set('page', '1');
    setSearchParams(next);
  };

  const exportCSV = () => {
    if (!data.items.length) {
      toast.error('No transactions available to export');
      return;
    }
    const headers = ['Order Number', 'Customer Name', 'Amount', 'Payment Gateway ID', 'Status', 'Date Time'];
    const rows = data.items.map((t) => [
      t.order?.orderNumber || 'N/A',
      `"${t.user?.name || 'Customer'}"`,
      t.amount,
      t.providerPaymentId || t.razorpayPaymentId || 'N/A',
      t.status,
      new Date(t.createdAt).toLocaleString(),
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Chandana_Aura_Transactions_Report_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Transactions CSV Report downloaded');
  };

  return (
    <div className="space-y-6 text-[#2B1810]">
      <div className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#2B1810]">Payment Transactions & Revenue Logs</h1>
          <p className="text-xs text-[#8A796C]">Monitor online Razorpay payments, authorized capture logs, and COD records.</p>
        </div>

        <div className="flex w-full xl:w-auto flex-col sm:flex-row sm:items-center gap-3">
          <AdminSearch value={searchInput} onChange={setSearchInput} onSearch={updateSearch} placeholder="Order, customer or payment ID" />
          <select
            className="bg-[#FAF8F5] border border-[#E0D6CC] text-xs font-semibold px-3 py-2 rounded focus:outline-none focus:border-[#C49A45]"
            value={status}
            onChange={(e) => { 
              const n = new URLSearchParams(searchParams); 
              if (e.target.value) n.set('status', e.target.value); 
              else n.delete('status'); 
              n.set('page', '1'); 
              setSearchParams(n); 
            }}
          >
            <option value="">All Payment Statuses</option>
            {['created', 'authorized', 'captured', 'paid', 'failed', 'refunded'].map((s) => (
              <option key={s} value={s}>{s.toUpperCase()}</option>
            ))}
          </select>

          <button
            onClick={exportCSV}
            className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-wider px-4 py-2 rounded shadow transition-colors"
          >
            ↓ Export CSV Report
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E8DFD8] rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-[#F5EFEB] text-[#2B1810] font-heading uppercase text-[11px] font-bold tracking-wider border-b border-[#E8DFD8]">
              <tr>
                <th className="p-4">Order Ref</th>
                <th className="p-4">Customer Name</th>
                <th className="p-4">Amount</th>
                <th className="p-4">Payment ID</th>
                <th className="p-4">Transaction Status</th>
                <th className="p-4">Date & Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8DFD8]">
              {loading ? (
                <tr><td className="p-6 text-center text-[#8A796C]" colSpan={6}>Loading transactions...</td></tr>
              ) : data.items.length === 0 ? (
                <tr><td className="p-6 text-center text-[#8A796C]" colSpan={6}>No transactions found.</td></tr>
              ) : (
                data.items.map((t) => (
                  <tr key={t._id} className="hover:bg-[#FAF8F5] transition-colors">
                    <td className="p-4 font-bold text-[#2B1810]">{t.order?.orderNumber || '—'}</td>
                    <td className="p-4 font-medium text-[#2B1810]">{t.user?.name || 'Customer'}</td>
                    <td className="p-4 font-bold text-[#2B1810]">₹{t.amount?.toFixed(2)}</td>
                    <td className="p-4 text-[#8A796C] font-mono text-[11px]">{t.providerPaymentId || t.razorpayPaymentId || '—'}</td>
                    <td className="p-4">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded ${
                        t.status === 'captured' ? 'bg-[#E8F3EB] text-[#2E5E3E]' : 'bg-[#F5EFEB] text-[#C49A45]'
                      }`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="p-4 text-[#8A796C]">{new Date(t.createdAt).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {data.pages > 1 && (
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: data.pages }, (_, index) => index + 1).map((pageNumber) => (
            <button
              type="button"
              key={pageNumber}
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.set('page', pageNumber);
                setSearchParams(next);
              }}
              className={`w-8 h-8 rounded text-xs font-bold ${pageNumber === page ? 'bg-[#C49A45] text-white' : 'bg-white border border-[#E8DFD8] text-[#2B1810]'}`}
            >
              {pageNumber}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
